/**
 * Scan configuration form for creating a new scan.
 * Allows selecting assets, scan type, and scanner engines.
 */

import { useState, type FormEvent } from 'react';
import type { Asset } from '@/types/asset';
import type { ScanType, ScannerEngine, ScanCreateRequest, ScanConfigRequest } from '@/types/scan';
import { scanTypes, engines } from '@/constants/scan'; // Import from constants file
import { useAgents } from '@/hooks/useAgents';

interface ScanConfigProps {
  assets: Asset[];
  onSubmit: (payload: ScanCreateRequest) => void;
  onCancel: () => void;
  isSubmitting?: boolean;
}

export default function ScanConfig({ assets, onSubmit, onCancel, isSubmitting }: ScanConfigProps) {
  const [name, setName] = useState('');
  const [scanType, setScanType] = useState<ScanType>('full');
  const [selectedAssets, setSelectedAssets] = useState<string[]>([]);
  const [selectedEngines, setSelectedEngines] = useState<ScannerEngine[]>([]);
  const [useAgent, setUseAgent] = useState(false);
  const [selectedAgentId, setSelectedAgentId] = useState('');
  const [error, setError] = useState('');

  const { data: agents = [] } = useAgents();
  const onlineAgents = agents.filter((a) => a.status === 'online' || a.status === 'scanning');

  function toggleAsset(id: string) {
    setSelectedAssets((prev) =>
      prev.includes(id) ? prev.filter((a) => a !== id) : [...prev, id],
    );
  }

  function toggleEngine(engine: ScannerEngine) {
    setSelectedEngines((prev) =>
      prev.includes(engine) ? prev.filter((e) => e !== engine) : [...prev, engine],
    );
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');
    if (!name.trim()) { setError('Scan name is required.'); return; }
    if (selectedAssets.length === 0) { setError('Select at least one asset to scan.'); return; }
    if(scanType==='custom'&&!selectedEngines.length){setError('Choose at least one scanner engine.');return;}
    if(useAgent&&!selectedAgentId){setError('Choose an execution agent.');return;}

    const payload: ScanCreateRequest = {
      name: name.trim(),
      scan_type: scanType,
      target_asset_ids: selectedAssets,
    };
    if (scanType === 'custom' && selectedEngines.length > 0) {
      payload.configs = selectedEngines.map((engine): ScanConfigRequest => ({
        scanner_engine: engine,
        enabled: true,
      }));
    }
    if (useAgent && selectedAgentId) {
      payload.agent_id = selectedAgentId;
    }
    onSubmit(payload);
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {error && (
        <div className="rounded-md bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
      )}

      <div>
        <label htmlFor="scan-name" className="block text-sm font-medium text-gray-700">Scan Name</label>
        <input
          id="scan-name"
          className="input-field mt-1"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Q1 2026 Full Assessment"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700">Scan Type</label>
        <div className="mt-2 space-y-2">
          {scanTypes.map((st) => (
            <label key={st.value} className="flex items-start gap-3 rounded-md border p-3 hover:bg-gray-50 cursor-pointer">
              <input
                type="radio"
                name="scanType"
                value={st.value}
                checked={scanType === st.value}
                onChange={() => setScanType(st.value)}
                className="mt-0.5"
              />
              <div>
                <p className="text-sm font-medium text-gray-900">{st.label}</p>
                <p className="text-xs text-gray-500">{st.description}</p>
              </div>
            </label>
          ))}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700">Target Assets</label>
        <div className="mt-2 max-h-48 space-y-1 overflow-y-auto rounded-md border p-2">
          {assets.length === 0 ? (
            <p className="py-2 text-center text-sm text-gray-500">No assets available.</p>
          ) : (
            assets.filter((a) => a.is_active).map((asset) => (
              <label key={asset.id} className="flex items-center gap-2 rounded px-2 py-1.5 hover:bg-gray-50 cursor-pointer">
                <input
                  type="checkbox"
                  checked={selectedAssets.includes(asset.id)}
                  onChange={() => toggleAsset(asset.id)}
                />
                <span className="text-sm text-gray-700">{asset.label ?? asset.value}</span>
                <span className="text-xs text-gray-400">({asset.value})</span>
              </label>
            ))
          )}
        </div>
      </div>

      {/* Optional: run on a remote agent */}
      {onlineAgents.length > 0 && (
        <div className="rounded-md border border-gray-200 p-3 space-y-2">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={useAgent}
              onChange={(e) => {
                setUseAgent(e.target.checked);
                if (!e.target.checked) setSelectedAgentId('');
                else if (onlineAgents.length > 0) setSelectedAgentId(onlineAgents[0]!.id);
              }}
            />
            <span className="text-sm font-medium text-gray-700">Run on remote agent</span>
          </label>
          {useAgent && (
            <select
              className="input-field"
              value={selectedAgentId}
              onChange={(e) => setSelectedAgentId(e.target.value)}
            >
              {onlineAgents.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name} ({a.status})
                </option>
              ))}
            </select>
          )}
        </div>
      )}

      {/* Engine selection (optional - only shown for custom scan type) */}
      {scanType === 'custom' && (
        <div>
          <label className="block text-sm font-medium text-gray-700">Scanner Engines</label>
          <div className="mt-2 space-y-1">
            {engines.map((eng) => (
              <label key={eng.value} className="flex items-center gap-2 rounded px-2 py-1.5 hover:bg-gray-50 cursor-pointer">
                <input
                  type="checkbox"
                  checked={selectedEngines.includes(eng.value)}
                  onChange={() => toggleEngine(eng.value)}
                />
                <span className="text-sm text-gray-700">{eng.label}</span>
              </label>
            ))}
          </div>
        </div>
      )}

      <div className="flex justify-end gap-3 pt-2">
        <button type="button" onClick={onCancel} className="btn-secondary">Cancel</button>
        <button type="submit" disabled={isSubmitting} className="btn-primary">
          {isSubmitting ? 'Starting...' : 'Start Scan'}
        </button>
      </div>
    </form>
  );
}
